import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import NavAcciones from '../../components/NavAcciones';
import VehicleDetailsForm from './components/VehicleDetailsForm';
import PhysicalConditionGrid from './components/PhysicalConditionGrid';
import ToolsAccessoriesChecklist from './components/ToolsAccessoriesChecklist';
import WarningLightsPanel from './components/WarningLightsPanel';
import QuickAnswersPanel from './components/QuickAnswersPanel';
import ClientAutocomplete from './components/ClientAutocomplete';
import { clientService } from '../../services/clientService';
import { vehicleService } from '../../services/vehicleService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';





import { vinDecodeService } from '../../services/vinDecodeService';
import Input from '../../components/ui/Input';
import { withTimeout } from '../../utils/supabaseTimeout';
import EvaModal from '../workshop-dashboard/components/EvaModal';





const VehicleReception = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdVehicleId, setCreatedVehicleId] = useState(null);

  // Client mode: 'search' | 'new' | 'existing'
  const [clientMode, setClientMode] = useState('search');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isNewVehicle, setIsNewVehicle] = useState(true);

  const [formData, setFormData] = useState({
    vehicleType: '',
    vin: '',
    plates: '',
    brand: '',
    model: '',
    year: '',
    engine: '',
    fuelType: '',
    odometer: '',
    color: ''
  });

  const [motivoVisita, setMotivoVisita] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const [clientData, setClientData] = useState({
    nombre: '',
    empresa: '',
    telefono: '',
    telefonoAlt: '',
    email: '',
    rfc: '',
    direccion: '',
    ciudad: '',
    estado: '',
    codigoPostal: ''
  });

  const [physicalCondition, setPhysicalCondition] = useState({});
  const [toolsChecklist, setToolsChecklist] = useState({});
  const [warningLights, setWarningLights] = useState({});
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [vinWarning, setVinWarning] = useState('');
  const [vinDecoding, setVinDecoding] = useState(false);
  const [vinDecodeMsg, setVinDecodeMsg] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [phoneSearching, setPhoneSearching] = useState(false);
  const phoneDebounceRef = useRef(null);
  const nameDebounceRef = useRef(null);
  const nameSuggestionsRef = useRef(null);
  const [evaModalOpen, setEvaModalOpen] = useState(false);

  // When a vehicle is selected from existing client, pre-fill form
  useEffect(() => {
    if (selectedVehicle) {
      setFormData(prev => ({
        ...prev,
        vehicleType: selectedVehicle?.tipo || '',
        vin: selectedVehicle?.vin || '',
        plates: selectedVehicle?.placas || '',
        brand: selectedVehicle?.marca || '',
        model: selectedVehicle?.modelo || '',
        year: selectedVehicle?.year || selectedVehicle?.año || '',
        engine: selectedVehicle?.motor || '',
        fuelType: selectedVehicle?.combustible || '',
        color: selectedVehicle?.color || ''
      }));
      setIsNewVehicle(false);
    }
  }, [selectedVehicle]);

  const handleClientSelected = (client, vehicles, vehicle, addNewVehicle) => {
    setSelectedClient(client);
    setClientMode('existing');

    const isDev = import.meta.env?.DEV;
    if (isDev) console.log('[PREFILL] client loaded', client);

    // Pre-fill client data — include address fields
    const prefillPayload = {
      nombre: client?.nombre || '',
      empresa: client?.empresa || '',
      telefono: client?.telefono || '',
      telefonoAlt: client?.telefonoAlt || client?.telefono_alt || '',
      email: client?.email || '',
      rfc: client?.rfc || '',
      direccion: client?.direccion || '',
      ciudad: client?.ciudad || '',
      estado: client?.estado || '',
      codigoPostal: client?.codigoPostal || client?.codigo_postal || ''
    };

    if (isDev) console.log('[PREFILL] prefill payload', prefillPayload);

    setClientData(prefillPayload);

    if (addNewVehicle) {
      setSelectedVehicle(null);
      setIsNewVehicle(true);
      setFormData({ vehicleType: '', vin: '', plates: '', brand: '', model: '', year: '', engine: '', fuelType: '', odometer: '', color: '' });
    } else if (vehicle) {
      setSelectedVehicle(vehicle);
    }
  };

  const handleNewClient = (initialName) => {
    setClientMode('new');
    setSelectedClient(null);
    setSelectedVehicle(null);
    setIsNewVehicle(true);
    if (initialName) {
      setClientData(prev => ({ ...prev, nombre: initialName }));
    }
  };

  // VIN duplicate check + hybrid decode
  const handleVinChange = async (vin) => {
    setFormData(prev => ({ ...prev, vin }));
    setVinWarning('');
    setVinDecodeMsg('');
    if (vin?.length === 17) {
      // 1. Check internal DB first
      const existing = await vehicleService?.getByVin(vin);
      if (existing) {
        setVinWarning(`⚠️ VIN ya registrado: ${existing?.marca} ${existing?.modelo} (${existing?.year}) - Cliente: ${existing?.client?.nombre || 'Desconocido'}`);
        // Auto-fill from existing vehicle
        setFormData(prev => ({
          ...prev,
          vin,
          brand: existing?.marca || prev?.brand,
          model: existing?.modelo || prev?.model,
          year: existing?.year ? String(existing?.year) : prev?.year,
          engine: existing?.motor || prev?.engine,
          fuelType: existing?.combustible || prev?.fuelType,
          color: existing?.color || prev?.color
        }));
        setVinDecodeMsg('✓ Datos cargados desde vehículo registrado');
        return;
      }
      // 2. Attempt VIN decode via NHTSA (non-blocking)
      setVinDecoding(true);
      try {
        const specs = await vinDecodeService?.decodeVin(vin);
        if (specs) {
          setFormData(prev => ({
            ...prev,
            vin,
            brand: specs?.make || prev?.brand,
            model: specs?.model || prev?.model,
            year: specs?.year ? String(specs?.year) : prev?.year,
            engine: specs?.engine || prev?.engine,
            fuelType: specs?.fuelType ? mapFuelType(specs?.fuelType) : prev?.fuelType
          }));
          setVinDecodeMsg(specs?.fromCache ? '✓ Datos cargados desde caché' : '✓ Datos decodificados desde VIN');
        } else {
          setVinDecodeMsg('No se pudo decodificar VIN, captura manual.');
        }
      } catch {
        setVinDecodeMsg('No se pudo decodificar VIN, captura manual.');
      } finally {
        setVinDecoding(false);
      }
    }
  };

  const mapFuelType = (nhtsaFuel) => {
    const f = nhtsaFuel?.toLowerCase() || '';
    if (f?.includes('gasoline') || f?.includes('gasolina')) return 'gasolina';
    if (f?.includes('diesel')) return 'diesel';
    if (f?.includes('electric') || f?.includes('eléctrico')) return 'electrico';
    if (f?.includes('hybrid') || f?.includes('híbrido')) return 'hibrido';
    return '';
  };

  const validateClientData = () => {
    const newErrors = {};
    if (!clientData?.nombre?.trim()) newErrors.nombre = 'El nombre completo es obligatorio';
    else if (clientData?.nombre?.trim()?.length < 3) newErrors.nombre = 'El nombre debe tener al menos 3 caracteres';
    if (!clientData?.telefono?.trim()) newErrors.telefono = 'El teléfono es obligatorio';
    else if (!/^\d{10}$/?.test(clientData?.telefono?.replace(/\s/g, ''))) newErrors.telefono = 'Ingrese un teléfono válido de 10 dígitos';
    if (clientData?.telefonoAlt?.trim() && !/^\d{10}$/?.test(clientData?.telefonoAlt?.replace(/\s/g, ''))) newErrors.telefonoAlt = 'Ingrese un teléfono válido de 10 dígitos';
    if (!clientData?.email?.trim()) newErrors.email = 'El correo electrónico es obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(clientData?.email)) newErrors.email = 'Ingrese un correo electrónico válido';
    if (clientData?.rfc?.trim() && !/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/?.test(clientData?.rfc?.toUpperCase())) newErrors.rfc = 'Ingrese un RFC válido (ej: XAXX010101000)';
    return newErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    const clientErrors = validateClientData();
    Object.assign(newErrors, clientErrors);
    if (!formData?.vehicleType) newErrors.vehicleType = 'Seleccione el tipo de vehículo';
    // VIN es opcional — no validar como requerido
    if (formData?.vin?.trim() && formData?.vin?.trim()?.length !== 17) newErrors.vin = 'El VIN debe tener exactamente 17 caracteres (o déjalo vacío)';
    if (!formData?.plates) newErrors.plates = 'Las placas son obligatorias';
    if (!formData?.brand) newErrors.brand = 'La marca es obligatoria';
    if (!formData?.model) newErrors.model = 'El modelo es obligatorio';
    if (!formData?.year) newErrors.year = 'El año es obligatorio';
    else if (formData?.year < 1900 || formData?.year > 2027) newErrors.year = 'Ingrese un año válido';
    if (!formData?.fuelType) newErrors.fuelType = 'Seleccione el tipo de combustible';
    if (!formData?.odometer) newErrors.odometer = 'El kilometraje es obligatorio';
    if (!formData?.color) newErrors.color = 'Seleccione el color';
    // NOTE: photos are optional — do NOT block save if damages have no photos
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleClientChange = (field, value) => {
    setClientData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => { const n = { ...prev }; delete n?.[field]; return n; });

    if (field === 'telefono') {
      const normalized = value?.replace(/[\s\-\(\)\+]/g, '')?.replace(/^52/, '')?.slice(0, 10);
      if (phoneDebounceRef?.current) clearTimeout(phoneDebounceRef?.current);
      if (normalized?.length >= 7) {
        setPhoneSearching(true);
        phoneDebounceRef.current = setTimeout(async () => {
          try {
            const found = await clientService?.search(normalized, 5);
            const match = found?.find(c => {
              const cPhone = c?.telefono?.replace(/[\s\-\(\)\+]/g, '')?.replace(/^52/, '');
              return cPhone === normalized;
            });
            if (match) {
              setClientData({
                nombre: match?.nombre || '',
                empresa: match?.empresa || '',
                telefono: match?.telefono || value,
                telefonoAlt: match?.telefonoAlt || match?.telefono_alt || '',
                email: match?.email || '',
                rfc: match?.rfc || '',
                direccion: match?.direccion || '',
                ciudad: match?.ciudad || '',
                estado: match?.estado || '',
                codigoPostal: match?.codigoPostal || match?.codigo_postal || ''
              });
              setSelectedClient(match);
              setClientMode('existing');
              if (import.meta.env?.DEV) console.log('[PHONE_AUTOCOMPLETE] Cliente encontrado por teléfono:', match?.nombre);
            }
          } catch {}
          finally { setPhoneSearching(false); }
        }, 400);
      } else {
        setPhoneSearching(false);
      }
    }

    if (field === 'nombre') {
      if (nameDebounceRef?.current) clearTimeout(nameDebounceRef?.current);
      if (value?.trim()?.length >= 2) {
        nameDebounceRef.current = setTimeout(async () => {
          try {
            const found = await clientService?.search(value?.trim(), 6);
            setClientSuggestions(found || []);
            setShowNameSuggestions(found?.length > 0);
          } catch { setClientSuggestions([]); setShowNameSuggestions(false); }
        }, 300);
      } else {
        setClientSuggestions([]);
        setShowNameSuggestions(false);
      }
    }
  };

  const handleSelectNameSuggestion = (client) => {
    setClientData({
      nombre: client?.nombre || '',
      empresa: client?.empresa || '',
      telefono: client?.telefono || '',
      telefonoAlt: client?.telefonoAlt || client?.telefono_alt || '',
      email: client?.email || '',
      rfc: client?.rfc || '',
      direccion: client?.direccion || '',
      ciudad: client?.ciudad || '',
      estado: client?.estado || '',
      codigoPostal: client?.codigoPostal || client?.codigo_postal || ''
    });
    setSelectedClient(client);
    setClientMode('existing');
    setShowNameSuggestions(false);
    setClientSuggestions([]);
    if (import.meta.env?.DEV) console.log('[NAME_AUTOCOMPLETE] Cliente seleccionado:', client?.nombre);
  };

  const formatPhoneNumber = (value) => value?.replace(/\D/g, '')?.slice(0, 10);
  const formatRFC = (value) => value?.toUpperCase()?.slice(0, 13);
  const formatPostalCode = (value) => value?.replace(/\D/g, '')?.slice(0, 5);

  // ── HANDLE SUBMIT: save client + vehicle + reception (NO work_order) ──
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (import.meta.env?.DEV) console.log('[RECEPTION_SUBMIT] handleSubmit triggered');
    if (!validateForm()) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setIsSubmitting(true);
    setSaveError('');
    const isDev = import.meta.env?.DEV;
    try {
      // ── STEP 1: Save / update client ──
      let client;
      if (selectedClient && clientMode === 'existing') {
        if (isDev) console.log('[SAVE] Actualizando cliente existente:', selectedClient?.id);
        client = await withTimeout(
          clientService?.update(selectedClient?.id, clientData),
          'clientService.update',
          20000
        );
      } else {
        if (isDev) console.log('[SAVE] Upsert cliente desde recepción:', clientData?.rfc || clientData?.email);
        client = await withTimeout(
          clientService?.upsertFromReception(clientData),
          'clientService.upsertFromReception',
          20000
        );
      }
      if (!client?.id) throw new Error('No se pudo guardar el cliente. Verifica permisos.');
      if (isDev) console.log('[SAVE] Cliente guardado, id:', client?.id);

      // ── STEP 2: Save / reuse vehicle ──
      let vehicle;
      if (selectedVehicle && !isNewVehicle) {
        vehicle = selectedVehicle;
        if (isDev) console.log('[SAVE] Reutilizando vehículo existente:', vehicle?.id);
      } else {
        if (formData?.vin) {
          const existingByVin = await withTimeout(
            vehicleService?.getByVin(formData?.vin),
            'vehicleService.getByVin',
            10000
          );
          if (existingByVin) {
            vehicle = existingByVin;
            if (isDev) console.log('[SAVE] Vehículo encontrado por VIN:', vehicle?.id);
          }
        }
        if (!vehicle) {
          // Resolve company_id: prefer from selected client, then from userProfile
          const resolvedCompanyId =
            selectedClient?.companyId ||
            selectedClient?.company_id ||
            userProfile?.company_id ||
            userProfile?.companyId ||
            null;

          const vehicleData = {
            clientId: client?.id,
            tipo: formData?.vehicleType,
            vin: formData?.vin,
            placas: formData?.plates,
            marca: formData?.brand,
            modelo: formData?.model,
            year: parseInt(formData?.year),
            motor: formData?.engine,
            combustible: formData?.fuelType,
            color: formData?.color
          };
          if (isDev) console.log('[SAVE] Creando vehículo:', vehicleData, 'company_id:', resolvedCompanyId);
          vehicle = await withTimeout(
            vehicleService?.create(vehicleData, resolvedCompanyId),
            'vehicleService.create',
            15000
          );
        }
      }
      if (!vehicle?.id) throw new Error('No se pudo guardar el vehículo. Verifica permisos.');
      if (isDev) console.log('[SAVE] Vehículo guardado, id:', vehicle?.id);

      // ── STEP 3: INSERT reception (pivote pre-autorización) ──
      const companyId = userProfile?.company_id || userProfile?.companyId || null;
      // Extract getUser() BEFORE the insert to avoid nested async inside payload
      const { data: authData } = await withTimeout(
        supabase?.auth?.getUser(),
        'auth.getUser',
        8000
      );
      const createdById = authData?.user?.id || null;

      const { data: reception, error: receptionError } = await withTimeout(
        supabase
          ?.from('receptions')
          ?.insert({
            client_id: client?.id,
            vehicle_id: vehicle?.id,
            status: 'received',
            odometer: formData?.odometer ? parseInt(formData?.odometer) : null,
            motivo_visita: motivoVisita || null,
            observaciones: observaciones || null,
            company_id: companyId,
            created_by: createdById
          })
          ?.select()
          ?.single(),
        'receptions.insert',
        15000
      );

      if (receptionError || !reception?.id) {
        throw new Error(receptionError?.message || 'No se pudo crear la recepción.');
      }
      console.log('[RECEPTION_CREATED]', reception?.id);

      // ── STEP 4: Upload evidence photos in background (non-blocking) ──
      // Photos are uploaded AFTER navigation so they never block the save flow.
      const hasPhotosToUpload = Object.values(physicalCondition || {})?.some(
        zone => zone?.photos?.some(p => p?.path)
      );
      if (!hasPhotosToUpload) {
        // Attempt to save physical condition records with already-uploaded photo paths
        const zonesWithPhotos = Object.entries(physicalCondition || {})?.filter(
          ([, zone]) => zone?.damages?.length > 0
        );
        if (zonesWithPhotos?.length > 0) {
          // Fire-and-forget: save condition records without blocking navigation
          Promise.resolve()?.then(async () => {
            try {
              const records = zonesWithPhotos?.map(([zoneId, zone]) => ({
                reception_id: reception?.id,
                vehicle_id: vehicle?.id,
                zone_id: zoneId,
                zone_name: {
                  front: 'Frente', rear: 'Parte Trasera', leftSide: 'Lado Izquierdo',
                  rightSide: 'Lado Derecho', roof: 'Techo', interior: 'Interior'
                }?.[zoneId] || zoneId,
                damages: zone?.damages || [],
                photos: (zone?.photos || [])?.map(p => p?.path)?.filter(Boolean)
              }));
              await supabase?.from('vehicle_physical_condition')?.insert(records);
              if (isDev) console.log('[PHYSICAL_CONDITION] Records saved in background');
            } catch (bgErr) {
              console.error('[PHYSICAL_CONDITION] Background save failed (non-blocking):', bgErr);
            }
          });
        }
      }

      // ── STEP 5: Navigate to Inspection with receptionId ──
      console.log('[NAVIGATE_TO_INSPECTION]', reception?.id);
      navigate(`/technical-inspection?receptionId=${reception?.id}`);
    } catch (error) {
      console.error('[RECEPTION_SUBMIT] Error:', error);
      const msg = error?.message || 'Por favor intente nuevamente.';
      setSaveError(`Error al guardar: ${msg}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} userRole="recepcion" />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-elevation-1">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect flex-shrink-0"
              >
                <Icon name={isSidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
              </button>
              <div className="min-w-0 pl-10 lg:pl-0">
                <h1 className="text-xl font-heading font-semibold text-foreground leading-tight">Recepción</h1>
                <p className="text-sm caption text-muted-foreground mt-1">Registro de cliente y vehículo</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate('/workshop-dashboard')}>
                <span className="hidden sm:inline">Volver al Dashboard</span>
                <span className="sm:hidden">Volver</span>
              </Button>
              <button
                onClick={() => setEvaModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors text-sm font-medium"
                title="Preguntar a EVA - Asistente IA"
              >
                <Icon name="Bot" size={16} />
                <span className="hidden sm:inline">Preguntar a EVA</span>
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <form onSubmit={handleSubmit} className="max-w-7xl mx-auto space-y-6">
            <NavAcciones
              onRegresar={() => navigate(-1)}
              onSalir={async () => { await supabase?.auth?.signOut(); navigate('/signin'); }}
              label="Recepción de Vehículo"
            />

            {/* Save error banner */}
            {saveError && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
                <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">Error al guardar</p>
                  <p className="text-sm text-destructive/80 mt-0.5">{saveError}</p>
                </div>
                <button type="button" onClick={() => setSaveError('')} className="text-destructive/60 hover:text-destructive">
                  <Icon name="X" size={16} />
                </button>
              </div>
            )}

            {/* ── SMART CLIENT SEARCH ── */}
            <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Búsqueda Inteligente de Cliente</h2>
                  <p className="text-sm text-muted-foreground">Busca un cliente existente o crea uno nuevo</p>
                </div>
              </div>
              <ClientAutocomplete
                onClientSelected={handleClientSelected}
                onNewClient={handleNewClient}
              />
              {clientMode === 'existing' && selectedClient && (
                <div className="mt-3 flex items-center gap-2 text-sm text-success">
                  <Icon name="CheckCircle2" size={16} />
                  <span>Cliente existente seleccionado. Sus datos han sido pre-llenados.</span>
                </div>
              )}
              {clientMode === 'existing' && selectedVehicle && !isNewVehicle && (
                <div className="mt-3 p-3 rounded-lg border-l-4 border-l-blue-500 border border-blue-200 bg-blue-50/60 shadow-sm flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Icon name="Car" size={15} className="text-blue-500 shrink-0" />
                    <span className="font-medium">Vehículo: {selectedVehicle?.marca} {selectedVehicle?.modelo} {selectedVehicle?.year || selectedVehicle?.año} — {selectedVehicle?.placas || selectedVehicle?.vin}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/vehicle-history/${selectedVehicle?.id}`)}
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-medium border border-primary/30 rounded-md px-3 py-1.5 transition-colors hover:bg-primary/5"
                  >
                    <Icon name="History" size={13} />
                    Ver Historial del Vehículo
                  </button>
                </div>
              )}
              {clientMode === 'new' && (
                <div className="mt-3 flex items-center gap-2 text-sm text-primary">
                  <Icon name="UserPlus" size={16} />
                  <span>Completando datos para nuevo cliente.</span>
                </div>
              )}
            </div>

            {/* ── CLIENT INFORMATION ── */}
            <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Información del Cliente</h2>
                  <p className="text-sm text-muted-foreground">
                    {clientMode === 'existing' ? 'Datos del cliente seleccionado (editables)' : 'Datos completos del propietario del vehículo'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Nombre Completo <span className="text-destructive ml-1 font-semibold">*</span></label>
                  <div className="relative" ref={nameSuggestionsRef}>
                    <Input type="text" placeholder="Ej: Juan Pérez García" value={clientData?.nombre} onChange={(e) => handleClientChange('nombre', e?.target?.value)} onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)} error={errors?.nombre} className="w-full" />
                    {showNameSuggestions && clientSuggestions?.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {clientSuggestions?.map((c) => (
                          <button
                            key={c?.id}
                            type="button"
                            onMouseDown={() => handleSelectNameSuggestion(c)}
                            className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-muted transition-colors text-left"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{c?.nombre}</p>
                              <p className="text-xs text-muted-foreground">{c?.telefono}{c?.email ? ` · ${c?.email}` : ''}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors?.nombre && <p className="text-sm text-destructive mt-1">{errors?.nombre}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Empresa (Opcional)</label>
                  <Input type="text" placeholder="Ej: Transportes del Norte SA de CV" value={clientData?.empresa} onChange={(e) => handleClientChange('empresa', e?.target?.value)} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Teléfono (WhatsApp) <span className="text-destructive ml-1 font-semibold">*</span></label>
                  <div className="relative">
                    <Input type="tel" placeholder="5512345678" value={clientData?.telefono} onChange={(e) => handleClientChange('telefono', formatPhoneNumber(e?.target?.value))} error={errors?.telefono} maxLength={10} className="w-full" />
                    {phoneSearching && (
                      <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  {errors?.telefono && <p className="text-sm text-destructive mt-1">{errors?.telefono}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Teléfono Alternativo (Opcional)</label>
                  <Input type="tel" placeholder="5587654321" value={clientData?.telefonoAlt} onChange={(e) => handleClientChange('telefonoAlt', formatPhoneNumber(e?.target?.value))} error={errors?.telefonoAlt} maxLength={10} className="w-full" />
                  {errors?.telefonoAlt && <p className="text-sm text-destructive mt-1">{errors?.telefonoAlt}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Correo Electrónico <span className="text-destructive ml-1 font-semibold">*</span></label>
                  <Input type="email" placeholder="cliente@ejemplo.com" value={clientData?.email} onChange={(e) => handleClientChange('email', e?.target?.value)} error={errors?.email} className="w-full" />
                  {errors?.email && <p className="text-sm text-destructive mt-1">{errors?.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">RFC <span className="text-muted-foreground font-normal">(Opcional)</span></label>
                  <Input type="text" placeholder="XAXX010101000" value={clientData?.rfc} onChange={(e) => handleClientChange('rfc', formatRFC(e?.target?.value))} error={errors?.rfc} maxLength={13} className="w-full" />
                  {errors?.rfc && <p className="text-sm text-destructive mt-1">{errors?.rfc}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Formato: 3-4 letras + 6 dígitos + 3 caracteres</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Dirección <span className="text-muted-foreground font-normal">(Opcional)</span></label>
                  <Input type="text" placeholder="Ej: Av. Insurgentes 1234" value={clientData?.direccion} onChange={(e) => handleClientChange('direccion', e?.target?.value)} error={errors?.direccion} className="w-full" />
                  {errors?.direccion && <p className="text-sm text-destructive mt-1">{errors?.direccion}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Ciudad</label>
                  <Input type="text" placeholder="Ej: Ciudad de México" value={clientData?.ciudad} onChange={(e) => handleClientChange('ciudad', e?.target?.value)} error={errors?.ciudad} className="w-full" />
                  {errors?.ciudad && <p className="text-sm text-destructive mt-1">{errors?.ciudad}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Estado</label>
                  <Input type="text" placeholder="Ej: CDMX" value={clientData?.estado} onChange={(e) => handleClientChange('estado', e?.target?.value)} error={errors?.estado} className="w-full" />
                  {errors?.estado && <p className="text-sm text-destructive mt-1">{errors?.estado}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Código Postal</label>
                  <Input type="text" placeholder="03100" value={clientData?.codigoPostal} onChange={(e) => handleClientChange('codigoPostal', formatPostalCode(e?.target?.value))} error={errors?.codigoPostal} maxLength={5} className="w-full" />
                  {errors?.codigoPostal && <p className="text-sm text-destructive mt-1">{errors?.codigoPostal}</p>}
                </div>
              </div>
            </div>

            {/* VIN Warning */}
            {vinWarning && (
              <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                <p className="text-sm text-warning">{vinWarning}</p>
              </div>
            )}

            {/* VIN Decode status */}
            {(vinDecoding || vinDecodeMsg) && (
              <div className={`rounded-lg p-3 flex items-center gap-2 text-sm ${
                vinDecoding ? 'bg-muted/50 text-muted-foreground' :
                vinDecodeMsg?.startsWith('✓') ? 'bg-success/10 text-success border border-success/20' :
                'bg-muted/50 text-muted-foreground'
              }`}>
                {vinDecoding
                  ? <><Icon name="Loader2" size={15} className="animate-spin" /><span>Decodificando VIN...</span></>
                  : <><Icon name={vinDecodeMsg?.startsWith('✓') ? 'CheckCircle2' : 'Info'} size={15} /><span>{vinDecodeMsg}</span></>}
              </div>
            )}

            <VehicleDetailsForm
              formData={formData}
              onChange={(data) => {
                if (data?.vin !== formData?.vin) handleVinChange(data?.vin);
                else setFormData(data);
              }}
              errors={errors}
            />

            {/* Motivo de Visita */}
            <div className="bg-card rounded-lg border border-border shadow-elevation-1 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-accent/10 rounded-lg">
                  <Icon name="MessageSquare" size={22} className="text-accent" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Motivo de Visita y Observaciones</h2>
                  <p className="text-sm text-muted-foreground">Describe el motivo de ingreso del vehículo</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Motivo de Visita</label>
                  <textarea value={motivoVisita} onChange={(e) => setMotivoVisita(e?.target?.value)} rows={3} placeholder="Describe el motivo de ingreso..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                  <QuickAnswersPanel fieldTarget="motivo_visita" label="Respuestas rápidas - Motivo" onSelect={(val) => setMotivoVisita(val)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Observaciones Generales</label>
                  <textarea value={observaciones} onChange={(e) => setObservaciones(e?.target?.value)} rows={3} placeholder="Condiciones generales, observaciones adicionales..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                  <QuickAnswersPanel fieldTarget="observaciones" label="Respuestas rápidas - Observaciones" onSelect={(val) => setObservaciones(val)} />
                </div>
              </div>
            </div>

            <PhysicalConditionGrid
              conditionData={physicalCondition}
              onChange={setPhysicalCondition}
              errors={errors}
            />

            <ToolsAccessoriesChecklist
              checklistData={toolsChecklist}
              onChange={setToolsChecklist}
            />

            <WarningLightsPanel
              warningLights={warningLights}
              onChange={setWarningLights}
            />

            <div className="flex flex-col sm:flex-row gap-4 justify-end sticky bottom-0 bg-background py-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/workshop-dashboard')}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isSubmitting}
                iconName="Send"
                iconPosition="right"
                className="sm:min-w-64"
              >
                {isSubmitting ? 'Guardando...' : 'Guardar y Continuar a Inspección'}
              </Button>
            </div>
          </form>
        </main>
      </div>
      <EvaModal
        isOpen={evaModalOpen}
        onClose={() => setEvaModalOpen(false)}
        contexto="Recepción"
        datosIniciales={{
          cliente: selectedClient ? { id: selectedClient?.id, nombre: selectedClient?.nombre || clientData?.nombre } : { nombre: clientData?.nombre },
          vehiculo: {
            marca: formData?.brand,
            modelo: formData?.model,
            year: formData?.year,
            vin: formData?.vin,
            placas: formData?.plates,
            motor: formData?.engine,
          },
        }}
      />
    </div>
  );
};

export default VehicleReception;